import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getApplicantStats } from '../utils/applicants';
import { getTeamStats } from '../utils/teams';
import { getProjectStats } from '../utils/projects';
import { getDiscardedFormsStats } from '../utils/discardedForms';
import { getFeedbackStats } from '../utils/formFeedback';
import AdminLayout from '../layouts/AdminLayout';
import { FiUsers, FiLayers, FiFolder, FiTrendingUp, FiClock, FiCheckCircle, FiXCircle, FiArrowRight, FiFileText, FiMessageSquare } from 'react-icons/fi';

const Dashboard = () => {
  const [stats, setStats] = useState({
    applicants: { total: 0, accepted: 0, rejected: 0, pending: 0 },
    teams: { total: 0, approved: 0, rejected: 0, pending: 0 },
    projects: { total: 0, submitted: 0, notSubmitted: 0 },
    discardedForms: { total: 0, averageFilled: 0, averageMissing: 0 },
    feedback: { total: 0, averageRating: 0, ratingCount: 0 }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError('');

      const [applicantsResult, teamsResult, projectsResult, discardedFormsResult, feedbackResult] = await Promise.all([
        getApplicantStats(),
        getTeamStats(),
        getProjectStats(),
        getDiscardedFormsStats(),
        getFeedbackStats()
      ]);

      if (applicantsResult.success && teamsResult.success && projectsResult.success) {
        setStats({
          applicants: applicantsResult.data,
          teams: teamsResult.data,
          projects: projectsResult.data,
          discardedForms: discardedFormsResult.success ? discardedFormsResult.stats : { total: 0, averageFilled: 0, averageMissing: 0 },
          feedback: feedbackResult.success ? feedbackResult.stats : { total: 0, averageRating: 0, ratingCount: 0 }
        });
      } else {
        setError('حدث خطأ في جلب الإحصائيات');
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError('حدث خطأ في جلب الإحصائيات');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center min-h-[80vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow mx-auto mb-4"></div>
            <p className="text-yellow">جاري التحميل...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center min-h-[80vh]">
          <div className="text-center bg-dark-blue p-8 rounded-2xl border-2 border-yellow max-w-md">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiXCircle className="text-yellow" size={32} />
            </div>
            <h2 className="text-xl font-semibold mb-2 text-yellow">خطأ</h2>
            <p className="text-white mb-4">{error}</p>
            <button
              onClick={fetchStats}
              className="px-6 py-2 bg-yellow text-[#05339c] rounded-lg hover:bg-yellow/80 transition-all duration-300 font-semibold"
            >
              إعادة المحاولة
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const statCards = [
    {
      title: 'المتقدمين',
      icon: FiUsers,
      total: stats.applicants.total,
      stats: [
        { label: 'مقبول', value: stats.applicants.accepted, color: 'text-green-400', bg: 'bg-green-500/20' },
        { label: 'مرفوض', value: stats.applicants.rejected, color: 'text-red-400', bg: 'bg-red-500/20' },
        { label: 'قيد المراجعة', value: stats.applicants.pending, color: 'text-yellow', bg: 'bg-yellow/20' }
      ],
      link: '/admin/applicants'
    },
    {
      title: 'الفرق',
      icon: FiLayers,
      total: stats.teams.total,
      stats: [
        { label: 'مقبول', value: stats.teams.approved, color: 'text-green-400', bg: 'bg-green-500/20' },
        { label: 'مرفوض', value: stats.teams.rejected, color: 'text-red-400', bg: 'bg-red-500/20' },
        { label: 'قيد المراجعة', value: stats.teams.pending, color: 'text-yellow', bg: 'bg-yellow/20' }
      ],
      link: '/admin/teams'
    },
    {
      title: 'المشاريع',
      icon: FiFolder,
      total: stats.projects.total,
      stats: [
        { label: 'مقدم', value: stats.projects.submitted, color: 'text-green-400', bg: 'bg-green-500/20' },
        { label: 'غير مقدم', value: stats.projects.notSubmitted, color: 'text-gray-400', bg: 'bg-gray-500/20' }
      ],
      link: '/admin/projects'
    }
  ];

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-yellow mb-2">
          مرحباً بك في <span className="text-white">لوحة التحكم</span>
        </h1>
        <p className="text-white/70">نظرة عامة على أداء الهاكاثون</p>
      </div>

      {/* Main Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {statCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <div key={index} className="bg-dark-blue rounded-2xl border-2 border-yellow overflow-hidden">
              {/* Header */}
              <div className="bg-yellow px-6 py-4 flex items-center justify-between">
                <div>
                  <p className="text-[#05339c]/80 text-sm font-medium">{card.title}</p>
                  <p className="text-[#05339c] text-3xl font-bold mt-1">{card.total}</p>
                </div>
                <div className="bg-[#05339c] p-3 rounded-xl">
                  <Icon className="text-yellow" size={32} />
                </div>
              </div>

              {/* Stats Details */}
              <div className="p-6 space-y-3">
                {card.stats.map((stat, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <span className="text-white/70 text-sm">{stat.label}</span>
                    <span className={`${stat.bg} ${stat.color} px-3 py-1 rounded-full text-sm font-semibold`}>
                      {stat.value}
                    </span>
                  </div>
                ))}

                <Link
                  to={card.link}
                  className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-white/10 text-yellow hover:text-yellow/80 font-medium text-sm transition-all"
                >
                  <span>عرض التفاصيل</span>
                  <FiArrowRight size={16} />
                </Link>
              </div>
            </div>
          );
        })}
      </div>

      {/* Additional Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-dark-blue border-2 border-yellow rounded-2xl p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <FiTrendingUp size={24} className="text-yellow" />
            <span className="text-white/60 text-sm">متوسط</span>
          </div>
          <p className="text-4xl font-bold text-yellow mb-1">
            {stats.applicants.averageAge ? Math.round(stats.applicants.averageAge) : 0}
          </p>
          <p className="text-white/70 text-sm">متوسط العمر (سنة)</p>
        </div>

        <div className="bg-dark-blue border-2 border-yellow rounded-2xl p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <FiCheckCircle size={24} className="text-yellow" />
            <span className="text-white/60 text-sm">معدل القبول</span>
          </div>
          <p className="text-4xl font-bold text-yellow mb-1">
            {stats.applicants.total > 0
              ? Math.round((stats.applicants.accepted / stats.applicants.total) * 100)
              : 0}%
          </p>
          <p className="text-white/70 text-sm">نسبة المقبولين</p>
        </div>

        <div className="bg-dark-blue border-2 border-yellow rounded-2xl p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <FiClock size={24} className="text-yellow" />
            <span className="text-white/60 text-sm">قيد المراجعة</span>
          </div>
          <p className="text-4xl font-bold text-yellow mb-1">
            {stats.applicants.pending + stats.teams.pending}
          </p>
          <p className="text-white/70 text-sm">طلبات معلقة</p>
        </div>

        <div className="bg-dark-blue border-2 border-yellow rounded-2xl p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <FiFolder size={24} className="text-yellow" />
            <span className="text-white/60 text-sm">التقديمات</span>
          </div>
          <p className="text-4xl font-bold text-yellow mb-1">
            {stats.projects.submitted}
          </p>
          <p className="text-white/70 text-sm">مشروع مقدم</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-dark-blue border-2 border-yellow rounded-2xl p-6">
        <h3 className="text-xl font-bold text-yellow mb-6">إجراءات سريعة</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          <Link
            to="/admin/applicants"
            className="group relative overflow-hidden bg-blue/20 rounded-xl p-6 hover:bg-blue/30 transition-all duration-300 border-2 border-blue"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="bg-blue p-3 rounded-lg text-yellow">
                <FiUsers size={24} />
              </div>
              <span className="text-yellow text-sm font-medium">
                {stats.applicants.pending} قيد المراجعة
              </span>
            </div>
            <h4 className="font-bold text-white mb-2">إدارة المتقدمين</h4>
            <p className="text-white/70 text-sm mb-4">مراجعة وموافقة طلبات التقديم</p>
            <div className="flex items-center text-yellow font-medium text-sm">
              <span>انتقال</span>
              <FiArrowRight className="mr-2 group-hover:translate-x-1 transition-transform" size={16} />
            </div>
          </Link>

          <Link
            to="/admin/teams"
            className="group relative overflow-hidden bg-blue/20 rounded-xl p-6 hover:bg-blue/30 transition-all duration-300 border-2 border-blue"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="bg-blue p-3 rounded-lg text-yellow">
                <FiLayers size={24} />
              </div>
              <span className="text-yellow text-sm font-medium">
                {stats.teams.pending} قيد المراجعة
              </span>
            </div>
            <h4 className="font-bold text-white mb-2">إدارة الفرق</h4>
            <p className="text-white/70 text-sm mb-4">مراجعة وموافقة تكوين الفرق</p>
            <div className="flex items-center text-yellow font-medium text-sm">
              <span>انتقال</span>
              <FiArrowRight className="mr-2 group-hover:translate-x-1 transition-transform" size={16} />
            </div>
          </Link>

          <Link
            to="/admin/projects"
            className="group relative overflow-hidden bg-blue/20 rounded-xl p-6 hover:bg-blue/30 transition-all duration-300 border-2 border-blue"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="bg-blue p-3 rounded-lg text-yellow">
                <FiFolder size={24} />
              </div>
              <span className="text-yellow text-sm font-medium">
                {stats.projects.submitted} مقدم
              </span>
            </div>
            <h4 className="font-bold text-white mb-2">إدارة المشاريع</h4>
            <p className="text-white/70 text-sm mb-4">مراجعة المشاريع المقدمة</p>
            <div className="flex items-center text-yellow font-medium text-sm">
              <span>انتقال</span>
              <FiArrowRight className="mr-2 group-hover:translate-x-1 transition-transform" size={16} />
            </div>
          </Link>

          <Link
            to="/admin/discarded"
            className="group relative overflow-hidden bg-blue/20 rounded-xl p-6 hover:bg-blue/30 transition-all duration-300 border-2 border-blue"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="bg-blue p-3 rounded-lg text-yellow">
                <FiFileText size={24} />
              </div>
              <span className="text-yellow text-sm font-medium">
                {stats.discardedForms.total} نموذج
              </span>
            </div>
            <h4 className="font-bold text-white mb-2">النماذج المهملة</h4>
            <p className="text-white/70 text-sm mb-4">تحليل النماذج غير المكتملة</p>
            <div className="flex items-center text-yellow font-medium text-sm">
              <span>انتقال</span>
              <FiArrowRight className="mr-2 group-hover:translate-x-1 transition-transform" size={16} />
            </div>
          </Link>

          <Link
            to="/admin/feedback"
            className="group relative overflow-hidden bg-blue/20 rounded-xl p-6 hover:bg-blue/30 transition-all duration-300 border-2 border-blue"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="bg-blue p-3 rounded-lg text-yellow">
                <FiMessageSquare size={24} />
              </div>
              <span className="text-yellow text-sm font-medium">
                {stats.feedback.total} ملاحظة
              </span>
            </div>
            <h4 className="font-bold text-white mb-2">ملاحظات المستخدمين</h4>
            <p className="text-white/70 text-sm mb-4">آراء وتقييمات المستخدمين</p>
            <div className="flex items-center text-yellow font-medium text-sm">
              <span>انتقال</span>
              <FiArrowRight className="mr-2 group-hover:translate-x-1 transition-transform" size={16} />
            </div>
          </Link>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Dashboard;
